/**
 * Loom floor. MASTER_PROMPT.md sections 7.4 and 8.3.
 *
 * Two things at once:
 *
 *  - a 90-tile heatmap that is the visual identity of the product, colour
 *    mapped by efficiency against capacity, so an idle floor is visible in one
 *    glance rather than inferred from missing rows (Phase 0 finding F5);
 *  - the shift entry for the floor: one row per loom, with dropdowns for the
 *    loom, its operator and its party; kilograms out, and every roll off the
 *    loom with its number and metres. Hours, manpower, power, the tape issued
 *    and the loss are for the whole floor and the whole shift, entered once —
 *    tape goes from the store to the floor, and no register says how much of
 *    it each loom drew.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, OfflineQueued } from "../../lib/api";
import { ops } from "../../lib/api2";
import {
  controls,
  materialsFor,
  type EntryContext,
  type LoomShiftIn,
} from "../../lib/controls";
import type { LoomCell, Shift, Sku, User } from "../../lib/types";
import { fmtPct, fmtQty, fmtShift, today } from "../../lib/format";
import { DrillCard } from "../../components/Drill";
import { MaterialSelect } from "../production/ShiftEntry";
import { parseLoomPaste, pasteSummary } from "./pasteLooms";

type Mode = "floor" | "entry";

/** Efficiency bands. Five steps, one hue — intensity carries the meaning and
 *  a label always accompanies it, so colour is never the only signal. */
function band(cell: LoomCell): string {
  if (!cell.entered || Number(cell.qty_mtr) === 0) {
    return cell.downtime_hours ? "down" : "idle";
  }
  const eff = cell.efficiency_pct === null ? 0 : Number(cell.efficiency_pct);
  if (eff >= 95) return "e5";
  if (eff >= 85) return "e4";
  if (eff >= 70) return "e3";
  if (eff >= 50) return "e2";
  return "e1";
}

interface Props {
  user: User;
  /** A loom floor entry to edit. */
  editId?: number | null;
  onDone?: () => void;
}

export function LoomFloor({ user, editId = null, onDone }: Props) {
  const [mode, setMode] = useState<Mode>(editId ? "entry" : "floor");
  const [businessDate, setBusinessDate] = useState(() => today());
  const [shift, setShift] = useState<Shift>("DAY");
  const [cells, setCells] = useState<LoomCell[] | null>(null);
  const [selected, setSelected] = useState<LoomCell | null>(null);
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
  } | null>(null);
  const [skus, setSkus] = useState<Sku[]>([]);

  // The SKU list is a master, not shift data — fetched once, not per date.
  // An operator has no masters access; the floor simply shows no SKU picker.
  useEffect(() => {
    ops.skus().then(setSkus).catch(() => setSkus([]));
  }, []);

  const load = useCallback(() => {
    setCells(null);
    api
      .loomFloor(businessDate, shift)
      .then(setCells)
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
  }, [businessDate, shift]);

  useEffect(load, [load]);

  const stats = useMemo(() => {
    if (!cells) return null;
    const running = cells.filter((c) => c.entered && Number(c.qty_mtr) > 0).length;
    const down = cells.filter((c) => Number(c.downtime_hours ?? 0) > 0).length;
    const totalMtr = cells.reduce((s, c) => s + Number(c.qty_mtr), 0);
    const rolls = cells.reduce((s, c) => s + Number(c.rolls_count ?? 0), 0);
    // Two different denominators, and the difference matters. Running
    // utilisation asks "were the looms we ran, run hard?"; floor utilisation
    // asks "how much of the floor did we use at all?". On a part-idle floor
    // they diverge sharply, so both are shown, labelled, and never merged.
    const runningCapacity = cells
      .filter((c) => c.entered && Number(c.qty_mtr) > 0)
      .reduce((s, c) => s + Number(c.capacity_mtr), 0);
    const floorCapacity = cells.reduce((s, c) => s + Number(c.capacity_mtr), 0);
    return {
      running,
      down,
      idle: cells.length - running - down,
      total: cells.length,
      totalMtr,
      rolls,
      floorCapacity,
      runningUtilisation: runningCapacity ? (totalMtr / runningCapacity) * 100 : 0,
      floorUtilisation: floorCapacity ? (totalMtr / floorCapacity) * 100 : 0,
    };
  }, [cells]);

  async function assignSku(loomNo: number, skuCode: string) {
    try {
      await api.assignLoomSku(loomNo, skuCode || null);
      setBanner({
        tone: "positive",
        text: skuCode
          ? `Loom ${loomNo} now stands assigned to ${skuCode}. Shifts already recorded are untouched.`
          : `Loom ${loomNo} has no standing SKU. Its metres will need one per shift.`,
      });
      load();
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  const drillFor = (label: string) => ({
    source: "loom",
    group: ["loom"],
    crumbs: [`${label} · ${businessDate} · ${fmtShift(shift)}`],
    filters: { from: businessDate, to: businessDate, shift },
  });

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">
            {editId ? "Edit loom floor shift" : "Loom floor"}
          </h2>
          <p className="page__sub">
            {mode === "floor"
              ? "Ninety looms, coloured by output against capacity"
              : `${user.name} · one row per loom, every roll with its number and metres`}
          </p>
        </div>
        <div className="page__controls">
          <label className="inputgroup">
            <span className="label">Date</span>
            <input type="date" value={businessDate}
                   onChange={(e) => setBusinessDate(e.target.value)} />
          </label>
          <label className="inputgroup">
            <span className="label">Shift</span>
            <select value={shift} onChange={(e) => setShift(e.target.value as Shift)}>
              <option value="DAY">Day</option>
              <option value="NIGHT">Night</option>
            </select>
          </label>
          {!editId && (
            <div className="segmented" role="tablist">
              <button className={mode === "floor" ? "is-active" : ""}
                      onClick={() => setMode("floor")} role="tab"
                      aria-selected={mode === "floor"}>
                Floor
              </button>
              <button className={mode === "entry" ? "is-active" : ""}
                      onClick={() => setMode("entry")} role="tab"
                      aria-selected={mode === "entry"}>
                Enter shift
              </button>
            </div>
          )}
        </div>
      </header>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="alert">
          {banner.text}
        </div>
      )}

      {mode === "entry" ? (
        <LoomEntry
          user={user}
          editId={editId}
          businessDate={businessDate}
          shift={shift}
          onDate={setBusinessDate}
          onShift={setShift}
          onSaved={(text) => {
            setBanner({ tone: "positive", text });
            if (editId) {
              onDone?.();
              return;
            }
            setMode("floor");
            load();
          }}
          onCancel={editId ? onDone : undefined}
        />
      ) : (
        <>
          {stats && (
            <div className="kpirow">
              {[
                { label: "Running", value: `${stats.running}`, sub: `of ${stats.total} looms` },
                {
                  label: "Idle",
                  value: `${stats.idle}`,
                  sub: stats.idle ? "no output recorded" : "all looms active",
                  tone: stats.idle > stats.total / 3 ? "warn" : undefined,
                },
                { label: "Metres", value: fmtQty(stats.totalMtr),
                  sub: `floor capacity ${fmtQty(stats.floorCapacity)}` },
                { label: "Rolls", value: fmtQty(stats.rolls), sub: "taken off the looms" },
                {
                  label: "Running looms",
                  value: fmtPct(stats.runningUtilisation),
                  sub: "of the capacity that ran",
                  tone: stats.runningUtilisation < 75 ? "warn" : undefined,
                },
                {
                  label: "Whole floor",
                  value: fmtPct(stats.floorUtilisation),
                  sub: `all ${stats.total} looms · ${fmtShift(shift)}`,
                  tone: stats.floorUtilisation < 60 ? "warn" : undefined,
                },
              ].map((k) => (
                <DrillCard key={k.label} drill={stats.running ? drillFor(k.label) : null}>
                  <span className="label">{k.label}</span>
                  <span className={`kpi-value num${k.tone === "warn" ? " kpi-value--warn" : ""}`}>
                    {k.value}
                  </span>
                  <span className="kpi__sub">{k.sub}</span>
                </DrillCard>
              ))}
            </div>
          )}

          {!cells ? (
            <div className="loomgrid">
              {Array.from({ length: 90 }, (_, i) => (
                <div key={i} className="skeleton loomtile" />
              ))}
            </div>
          ) : (
            <>
              <div className="loomgrid" role="list">
                {cells.map((cell) => (
                  <button
                    key={cell.loom_no}
                    role="listitem"
                    className={`loomtile loomtile--${band(cell)}${
                      selected?.loom_no === cell.loom_no ? " loomtile--selected" : ""
                    }`}
                    onClick={() =>
                      setSelected(selected?.loom_no === cell.loom_no ? null : cell)
                    }
                    title={`Loom ${cell.loom_no} · ${fmtQty(cell.qty_mtr)} m · ${
                      cell.efficiency_pct === null
                        ? "no capacity on record"
                        : fmtPct(cell.efficiency_pct)
                    }`}
                  >
                    <span className="loomtile__no">{cell.loom_no}</span>
                    <span className="loomtile__eff">
                      {cell.entered && Number(cell.qty_mtr) > 0
                        ? `${Math.round(Number(cell.efficiency_pct ?? 0))}`
                        : "—"}
                    </span>
                  </button>
                ))}
              </div>

              <div className="legend">
                <span className="label">Efficiency</span>
                {[
                  ["idle", "Idle"],
                  ["e1", "Under 50%"],
                  ["e2", "50–70%"],
                  ["e3", "70–85%"],
                  ["e4", "85–95%"],
                  ["e5", "95%+"],
                ].map(([cls, text]) => (
                  <span key={cls} className="legend__item">
                    <span className={`legend__swatch loomtile--${cls}`} />
                    {text}
                  </span>
                ))}
              </div>

              {selected && (
                <div className="loomdetail card">
                  <h3>Loom {selected.loom_no}</h3>
                  <dl>
                    <div><dt>Output</dt><dd className="num mono">{fmtQty(selected.qty_mtr)} m · {fmtQty(selected.qty_kg, 1)} kg</dd></div>
                    <div><dt>Input</dt><dd className="num mono">{selected.input_kg ? `${fmtQty(selected.input_kg, 1)} kg` : "—"}</dd></div>
                    <div><dt>Rolls</dt><dd className="num mono">{selected.rolls_count ?? "—"}</dd></div>
                    <div><dt>Capacity</dt><dd className="num mono">{fmtQty(selected.capacity_mtr)} m</dd></div>
                    <div><dt>Efficiency</dt><dd className="num mono">{selected.efficiency_pct === null ? "—" : fmtPct(selected.efficiency_pct)}</dd></div>
                    <div><dt>Operator</dt><dd>{selected.operator ?? "—"}</dd></div>
                    <div><dt>Party</dt><dd>{selected.party_name ?? "—"}</dd></div>
                    <div><dt>Status</dt><dd>{selected.status.toLowerCase()}</dd></div>
                    {skus.length > 0 && (
                      <div>
                        <dt>SKU</dt>
                        <dd>
                          <select
                            value={selected.sku_code ?? ""}
                            onChange={(e) => {
                              setSelected({ ...selected, sku_code: e.target.value || null });
                              assignSku(selected.loom_no, e.target.value);
                            }}
                          >
                            <option value="">Not assigned</option>
                            {skus.map((s) => (
                              <option key={s.code} value={s.code}>{s.code}</option>
                            ))}
                          </select>
                          <span className="cell__unit">
                            {selected.sku_from_shift
                              ? "recorded on this shift · changing this sets the standing assignment, not the shift already entered"
                              : "the standing assignment · a shift may override it"}
                          </span>
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// -- Entry -------------------------------------------------------------------

interface RollDraft {
  key: string;
  roll_no: string;
  metres: string;
}

interface LoomDraft {
  key: string;
  loom_no: string;
  operator_id: string;
  party_id: string;
  input_item_code: string;
  input_kg: string;
  output_item_code: string;
  output_kg: string;
  rolls: RollDraft[];
  error?: string;
}

const uid = () => Math.random().toString(36).slice(2);
const num = (value: string) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const blankRoll = (): RollDraft => ({ key: uid(), roll_no: "", metres: "" });

function LoomEntry({
  user,
  editId,
  businessDate,
  shift,
  onDate,
  onShift,
  onSaved,
  onCancel,
}: {
  user: User;
  editId: number | null;
  businessDate: string;
  shift: Shift;
  onDate: (d: string) => void;
  onShift: (s: Shift) => void;
  onSaved: (message: string) => void;
  onCancel?: () => void;
}) {
  const [ctx, setCtx] = useState<EntryContext | null>(null);
  const [rows, setRows] = useState<LoomDraft[]>([]);
  const [hours, setHours] = useState("");
  const [manpower, setManpower] = useState("");
  const [power, setPower] = useState("");
  const [inputItem, setInputItem] = useState("");
  const [inputKg, setInputKg] = useState("");
  const [lossItem, setLossItem] = useState("");
  const [lossKg, setLossKg] = useState("");
  const [lossTouched, setLossTouched] = useState(false);
  const [reasonCodeId, setReasonCodeId] = useState<number | "">("");
  const [note, setNote] = useState("");
  const [editReason, setEditReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Set the moment Submit is pressed, before React has re-rendered with
  // `busy` — a second click in that gap must not send the shift twice.
  const sending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteNote, setPasteNote] = useState<string | null>(null);

  const machine = ctx?.machines.find((m) => m.code === "LOOM_FLOOR");
  const inputs = materialsFor(ctx, machine, "INPUT");
  const outputs = materialsFor(ctx, machine, "OUTPUT");
  const wastes = materialsFor(ctx, machine, "WASTE");
  const tolerance = machine ? Number(machine.tolerance_pct) * 100 : 0;

  const defaultInput = machine?.materials.INPUT?.[0] ?? "TAPE";
  const defaultOutput = machine?.materials.OUTPUT?.[0] ?? "FABRIC_KG";

  const blankLoom = useCallback(
    (loomNo = ""): LoomDraft => {
      const party = ctx?.looms.find((l) => String(l.loom_no) === loomNo)?.party_id;
      return {
        key: uid(),
        loom_no: loomNo,
        operator_id: "",
        party_id: party ? String(party) : "",
        input_item_code: defaultInput,
        input_kg: "",
        output_item_code: defaultOutput,
        output_kg: "",
        rolls: [blankRoll()],
      };
    },
    [ctx, defaultInput, defaultOutput]
  );

  useEffect(() => {
    controls
      .context()
      .then((c) => {
        setCtx(c);
        const floor = c.machines.find((m) => m.code === "LOOM_FLOOR");
        setInputItem(floor?.materials.INPUT?.[0] ?? "TAPE");
        setLossItem(floor?.materials.WASTE?.[0] ?? "WASTE_PIP_CUT");
      })
      .catch((e: ApiError) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!ctx || editId || rows.length) return;
    setRows([blankLoom()]);
  }, [ctx, editId, rows.length, blankLoom]);

  // Editing: the entry as it stands.
  useEffect(() => {
    if (!editId || !ctx) return;
    controls
      .entry(editId)
      .then((entry) => {
        onDate(entry.business_date);
        onShift(entry.shift);
        setHours(entry.running_hours ?? "");
        setManpower(entry.manpower_count?.toString() ?? "");
        setPower(entry.power_units ?? "");
        // The tape issue for the floor is the one with no loom against it.
        const issued = entry.lines.find((l) => l.type === "ISSUE" && l.loom_id === null);
        if (issued) {
          setInputItem(issued.item_code ?? defaultInput);
          setInputKg(issued.qty ?? "");
        }
        const loss = entry.lines.find((l) => l.type === "LOSS");
        if (loss) {
          setLossItem(loss.item_code);
          setLossKg(loss.qty);
          setLossTouched(true);
        }
        setReasonCodeId(entry.reason_code_id ?? "");
        setNote(entry.variance_note ?? "");
        setRows(
          entry.looms.map((l) => ({
            key: uid(),
            loom_no: String(l.loom_no),
            operator_id: l.operator_id ? String(l.operator_id) : "",
            party_id: l.party_id ? String(l.party_id) : "",
            input_item_code: l.input_item_code ?? defaultInput,
            input_kg: l.input_kg ?? "",
            output_item_code: l.output_item_code ?? defaultOutput,
            output_kg: l.output_kg ?? "",
            rolls: l.rolls.length
              ? [...l.rolls.map((r) => ({ key: uid(), roll_no: r.roll_no, metres: r.metres })), blankRoll()]
              : [blankRoll()],
          }))
        );
        if (!entry.can_change) setError(entry.cannot_change_because);
      })
      .catch((e: ApiError) => setError(e.message));
    // Once, when the context has arrived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, ctx]);

  const totals = useMemo(() => {
    // The floor's tape issue, plus anything an older entry booked per loom.
    const input = num(inputKg) + rows.reduce((s, r) => s + num(r.input_kg), 0);
    const output = rows.reduce((s, r) => s + num(r.output_kg), 0);
    const rolls = rows.reduce(
      (s, r) => s + r.rolls.filter((x) => x.roll_no || num(x.metres) > 0).length, 0);
    const metres = rows.reduce(
      (s, r) => s + r.rolls.reduce((m, x) => m + num(x.metres), 0), 0);
    return { input, output, rolls, metres };
  }, [rows, inputKg]);

  const autoLoss = Math.max(totals.input - totals.output, 0);
  const loss = lossTouched ? num(lossKg) : autoLoss;
  const variance = totals.input - totals.output - loss;
  const pct = totals.input ? (variance / totals.input) * 100 : 0;
  const within = totals.input === 0 || Math.abs(pct) <= tolerance + 1e-9;
  const reasons = ctx?.reason_codes ?? [];
  const selectedReason = reasons.find((r) => r.id === reasonCodeId);

  function patch(index: number, change: Partial<LoomDraft>) {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...change, error: undefined };
      if (change.loom_no !== undefined && !next[index].party_id) {
        const party = ctx?.looms.find((l) => String(l.loom_no) === change.loom_no)?.party_id;
        if (party) next[index].party_id = String(party);
      }
      return next;
    });
  }

  function patchRoll(index: number, rollIndex: number, change: Partial<RollDraft>) {
    setRows((prev) => {
      const next = [...prev];
      const rolls = [...next[index].rolls];
      rolls[rollIndex] = { ...rolls[rollIndex], ...change };
      // Always one spare roll to type into.
      if (rollIndex === rolls.length - 1 && (change.roll_no || change.metres)) {
        rolls.push(blankRoll());
      }
      next[index] = { ...next[index], rolls };
      return next;
    });
  }

  function removeRoll(index: number, rollIndex: number) {
    setRows((prev) => {
      const next = [...prev];
      const rolls = next[index].rolls.filter((_, i) => i !== rollIndex);
      next[index] = { ...next[index], rolls: rolls.length ? rolls : [blankRoll()] };
      return next;
    });
  }

  /**
   * Paste from Excel — the plant's own loom production register or a roll
   * register, read by pasteLooms.ts, which explains both shapes.
   */
  function applyPaste() {
    if (!ctx) return;
    const result = parseLoomPaste(pasteText, shift, ctx);
    if (!result.looms.length) {
      setError(
        result.idle
          ? `Nothing to add — all ${result.idle} looms on that paste stood still.`
          : "Nothing on that paste looks like a loom line."
      );
      return;
    }
    const pasted: LoomDraft[] = result.looms.map((l) => ({
      ...blankLoom(l.loom_no),
      operator_id: l.operator_id,
      party_id: l.party_id || blankLoom(l.loom_no).party_id,
      output_kg: l.output_kg,
      rolls: [...l.rolls.map((r) => ({ key: uid(), ...r })), blankRoll()],
    }));
    // Pasting again — a corrected register, or the same one by mistake —
    // replaces the looms already on the sheet rather than adding them twice.
    const onPaste = new Set(pasted.map((p) => p.loom_no));
    const replaced = rows.filter((r) => r.loom_no && onPaste.has(r.loom_no)).length;
    setRows((prev) => [
      ...prev.filter((r) => (r.loom_no || r.input_kg || r.output_kg) && !onPaste.has(r.loom_no)),
      ...pasted,
    ]);
    // Tape is the shift's, not a loom's, so a pasted input column lands on
    // the floor total — and never over a figure already typed there.
    if (result.inputKg && !inputKg) setInputKg(result.inputKg);
    setPasteOpen(false);
    setPasteText("");
    setError(null);
    setPasteNote(
      pasteSummary(result) +
        (replaced
          ? ` ${replaced} loom${replaced === 1 ? " was" : "s were"} already on the sheet and ${replaced === 1 ? "was" : "were"} replaced by the paste, not added twice.`
          : "")
    );
  }

  async function submit() {
    if (sending.current) return;
    setError(null);
    // What a row says the loom did. Kilograms off a loom are often not
    // weighed — the register carries metres — so either will do.
    const ran = (r: LoomDraft) =>
      num(r.output_kg) > 0 || r.rolls.some((x) => x.roll_no.trim() || num(x.metres) > 0);
    const filled = (r: LoomDraft) => r.loom_no || num(r.input_kg) || ran(r);
    const lines = rows.filter(filled);
    const seen = new Set<string>();
    const marked = rows.map((r) => {
      if (!filled(r)) return r;
      if (!r.loom_no) return { ...r, error: "Choose the loom." };
      if (seen.has(r.loom_no)) return { ...r, error: `Loom ${r.loom_no} is on two rows.` };
      seen.add(r.loom_no);
      if (!ran(r)) return { ...r, error: "Enter the metres off this loom, or its kilograms." };
      return r;
    });
    if (marked.some((r) => r.error)) {
      setRows(marked);
      setError("Fix the marked looms first.");
      return;
    }
    if (!lines.length) {
      setError("Add at least one loom.");
      return;
    }
    if (!within && !reasonCodeId) {
      setError(
        `${fmtQty(Math.abs(variance), 2)} kg is unaccounted — ${fmtPct(Math.abs(pct))}, outside the ${fmtPct(tolerance)} tolerance. Choose a reason.`
      );
      return;
    }
    if (selectedReason?.requires_note && !note.trim()) {
      setError(`"${selectedReason.description}" needs a note.`);
      return;
    }
    if (editId && editReason.trim().length < 3) {
      setError("Say why this entry is being changed.");
      return;
    }

    const body: LoomShiftIn = {
      business_date: businessDate,
      shift,
      running_hours: hours ? num(hours) : null,
      manpower_count: manpower ? Math.round(num(manpower)) : null,
      power_units: power ? num(power) : null,
      input_kg: inputKg ? num(inputKg) : null,
      input_item_code: inputItem || null,
      loss_kg: loss > 0 ? Number(loss.toFixed(3)) : null,
      loss_item_code: lossItem || null,
      submit: true,
      reason_code_id: reasonCodeId === "" ? null : reasonCodeId,
      variance_note: note || null,
      lines: lines.map((r) => ({
        loom_no: Number(r.loom_no),
        operator_id: r.operator_id ? Number(r.operator_id) : null,
        party_id: r.party_id ? Number(r.party_id) : null,
        input_item_code: r.input_item_code || null,
        input_kg: num(r.input_kg),
        output_item_code: r.output_item_code || null,
        qty_kg: num(r.output_kg),
        rolls: r.rolls
          .filter((x) => x.roll_no.trim() || num(x.metres) > 0)
          .map((x) => ({ roll_no: x.roll_no.trim(), metres: num(x.metres) })),
      })),
    };

    sending.current = true;
    setBusy(true);
    try {
      if (editId) {
        await controls.editLoom(editId, editReason.trim(), body);
        onSaved("Loom floor entry changed. The old figures are kept in its history.");
        return;
      }
      const res = await controls.recordLoomShift(body);
      onSaved(
        `${res.looms_entered} looms · ${fmtQty(res.total_rolls)} rolls · ${fmtQty(res.total_mtr)} m · ${fmtQty(res.total_input_kg, 1)} kg in, ${fmtQty(res.total_kg, 1)} kg out.` +
          (res.over_capacity.length ? ` Above capacity: ${res.over_capacity.join("; ")}.` : "")
      );
    } catch (err) {
      if (err instanceof OfflineQueued) {
        onSaved("No connection. The shift is saved on this device and will sync.");
        return;
      }
      const e = err as ApiError;
      setError(e.blocked?.message ?? e.message);
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }

  if (ctx && !machine) {
    return (
      <div className="state state--empty">
        <span className="state__glyph" aria-hidden="true">▦</span>
        <h3>The loom floor is not one of your machines</h3>
        <p>An administrator adds it to your login in Settings.</p>
      </div>
    );
  }

  const takenLooms = new Set(rows.map((r) => r.loom_no).filter(Boolean));

  return (
    <>
      {error && <div className="banner banner--negative" role="alert">{error}</div>}
      {pasteNote && (
        <div className="banner banner--warning" role="status">
          {pasteNote}
          <button className="btn btn--ghost btn--auto" onClick={() => setPasteNote(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="formgrid">
        <label className="inputgroup">
          <span className="label">Running hours — whole shift</span>
          <input className="num" inputMode="decimal" value={hours}
                 onChange={(e) => setHours(e.target.value)} />
        </label>
        <label className="inputgroup">
          <span className="label">Manpower — whole shift</span>
          <input className="num" inputMode="numeric" value={manpower}
                 onChange={(e) => setManpower(e.target.value)} />
        </label>
        <label className="inputgroup">
          <span className="label">Power kWh — whole shift</span>
          <input className="num" inputMode="decimal" value={power}
                 onChange={(e) => setPower(e.target.value)} />
        </label>
        <div className="page__controls">
          <button className="btn btn--ghost btn--auto" onClick={() => setPasteOpen((o) => !o)}>
            Paste from Excel
          </button>
        </div>
      </div>

      {pasteOpen && (
        <div className="card">
          <p className="section-note">
            Paste the plant's loom production register as it stands — L.NO, MODEL, PARTY, the
            day and night names with their metres — or a roll register of loom, operator, party,
            input kg, output kg, roll number, metres. Keep the header row and the columns are
            read from it; the names and metres for the {fmtShift(shift).toLowerCase()} shift are
            the ones taken. Lines for the same loom become one row, and looms that stood still
            are left off.
          </p>
          <textarea rows={6} value={pasteText} onChange={(e) => setPasteText(e.target.value)}
                    style={{ width: "100%" }} placeholder={"L.NO\tMODEL\tPARTY\tNAME DAY\t12 HOUR\tNAME NIGHT\t12 HOUR\n10\tBSW\tTOPCEM\tRamkebul Pandit\t850\tPappu Kumar\t928"} />
          <div className="page__footer">
            <button className="btn btn--auto" onClick={applyPaste} disabled={!pasteText.trim()}>
              Add these looms
            </button>
          </div>
        </div>
      )}

      <div className="entry__body">
        <div className="loomrows">
          {rows.map((row, index) => {
            const metres = row.rolls.reduce((m, x) => m + num(x.metres), 0);
            const rollCount = row.rolls.filter((x) => x.roll_no || num(x.metres) > 0).length;
            return (
              <div key={row.key} className={`loomrow${row.error ? " loomrow--error" : ""}`}>
                <label className="inputgroup">
                  <span className="label">Loom</span>
                  <select value={row.loom_no} onChange={(e) => patch(index, { loom_no: e.target.value })}>
                    <option value="">No.</option>
                    {(ctx?.looms ?? []).map((l) => (
                      <option key={l.loom_no} value={String(l.loom_no)}
                              disabled={takenLooms.has(String(l.loom_no)) && row.loom_no !== String(l.loom_no)}>
                        {l.loom_no}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="inputgroup">
                  <span className="label">Operator</span>
                  <select value={row.operator_id} onChange={(e) => patch(index, { operator_id: e.target.value })}>
                    <option value="">{ctx?.operators.length ? "Choose…" : "None set up"}</option>
                    {(ctx?.operators ?? []).map((o) => (
                      <option key={o.id} value={String(o.id)}>{o.name}</option>
                    ))}
                  </select>
                </label>
                <label className="inputgroup">
                  <span className="label">Party</span>
                  <select value={row.party_id} onChange={(e) => patch(index, { party_id: e.target.value })}>
                    <option value="">Choose…</option>
                    {(ctx?.parties ?? []).map((p) => (
                      <option key={p.id} value={String(p.id)}>{p.name}</option>
                    ))}
                  </select>
                </label>
                <label className="inputgroup">
                  <span className="label">Output material</span>
                  <MaterialSelect value={row.output_item_code} options={outputs}
                                  onChange={(code) => patch(index, { output_item_code: code })} />
                </label>
                <label className="inputgroup">
                  <span className="label">Output kg</span>
                  <input className="num mono" inputMode="decimal" value={row.output_kg}
                         onChange={(e) => patch(index, { output_kg: e.target.value })} />
                </label>
                <button className="grid__remove" aria-label={`Remove loom row ${row.loom_no}`}
                        onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}>
                  ×
                </button>

                <div className="loomrow__rolls">
                  <span className="label">Rolls</span>
                  {row.rolls.map((roll, rollIndex) => (
                    <span key={roll.key} className="roll">
                      <input placeholder="Roll no." value={roll.roll_no}
                             aria-label={`Loom ${row.loom_no} roll ${rollIndex + 1} number`}
                             onChange={(e) => patchRoll(index, rollIndex, { roll_no: e.target.value })} />
                      <input className="num mono" placeholder="Metres" inputMode="decimal"
                             aria-label={`Loom ${row.loom_no} roll ${rollIndex + 1} metres`}
                             value={roll.metres}
                             onChange={(e) => patchRoll(index, rollIndex, { metres: e.target.value })} />
                      {(roll.roll_no || roll.metres) && (
                        <button className="grid__remove" aria-label="Remove roll" tabIndex={-1}
                                onClick={() => removeRoll(index, rollIndex)}>×</button>
                      )}
                    </span>
                  ))}
                  <span className="loomrow__sum">
                    {rollCount} roll{rollCount === 1 ? "" : "s"} · {fmtQty(metres)} m
                  </span>
                </div>
                {row.error && <span className="grid__error">{row.error}</span>}
              </div>
            );
          })}
          <div>
            <button className="btn btn--ghost btn--auto" onClick={() => setRows((prev) => [...prev, blankLoom()])}>
              + Add loom
            </button>
          </div>
        </div>

        <aside className="balance">
          <h3 className="balance__title">Floor balance</h3>
          <dl className="balance__list">
            <div><dt>Looms</dt><dd className="num mono">{rows.filter((r) => r.loom_no).length}</dd></div>
            <div><dt>Input</dt><dd className="num mono">{fmtQty(totals.input, 2)}</dd></div>
            <div><dt>Output</dt><dd className="num mono">{fmtQty(totals.output, 2)}</dd></div>
            <div><dt>Rolls · metres</dt><dd className="num mono">{totals.rolls} · {fmtQty(totals.metres)}</dd></div>
          </dl>

          <label className="inputgroup">
            <span className="label">Input material</span>
            <MaterialSelect value={inputItem} options={inputs} onChange={setInputItem} />
          </label>
          <label className="inputgroup">
            <span className="label">Input kg — whole shift</span>
            <input className="num mono" inputMode="decimal" value={inputKg}
                   onChange={(e) => setInputKg(e.target.value)} />
            <span className="grid__auto">what the store issued to the floor</span>
          </label>

          <label className="inputgroup">
            <span className="label">Loss material</span>
            <MaterialSelect value={lossItem} options={wastes} onChange={setLossItem} />
          </label>
          <label className="inputgroup">
            <span className="label">Loss kg — whole shift</span>
            <input className="num mono" inputMode="decimal"
                   value={lossTouched ? lossKg : autoLoss ? String(Number(autoLoss.toFixed(3))) : ""}
                   onChange={(e) => {
                     setLossTouched(true);
                     setLossKg(e.target.value);
                   }} />
            <span className="grid__auto">
              {lossTouched ? (
                <button type="button" className="btn btn--ghost btn--auto btn--sm"
                        onClick={() => setLossTouched(false)}>
                  Use {fmtQty(autoLoss, 2)}
                </button>
              ) : "auto: input − output"}
            </span>
          </label>

          <div className={`balance__result balance__result--${totals.input === 0 ? "idle" : within ? "ok" : "off"}`}>
            <span className="label">Unaccounted</span>
            <span className="balance__value num mono">{fmtQty(variance, 2)}</span>
            <span className="balance__pct">
              {totals.input === 0 ? "Nothing entered yet" : `${fmtPct(pct)} · tolerance ${fmtPct(tolerance)}`}
            </span>
          </div>

          <label className="inputgroup">
            <span className="label">Reason {within ? "(optional)" : "— required"}</span>
            <select value={reasonCodeId}
                    onChange={(e) => setReasonCodeId(e.target.value === "" ? "" : Number(e.target.value))}>
              <option value="">Choose…</option>
              {reasons.map((r) => (
                <option key={r.id} value={r.id}>{r.description}</option>
              ))}
            </select>
          </label>
          {(selectedReason?.requires_note || note) && (
            <label className="inputgroup">
              <span className="label">Note</span>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
          )}
          {editId && (
            <label className="inputgroup">
              <span className="label">Why is this being changed? — required</span>
              <textarea rows={2} value={editReason} onChange={(e) => setEditReason(e.target.value)} />
            </label>
          )}

          <button className="btn" onClick={submit} disabled={busy || !ctx}>
            {busy ? <span className="spinner" /> : null}
            {busy ? "Saving" : editId ? "Save changes" : "Submit shift"}
          </button>
          {onCancel && (
            <button className="btn btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          )}
          <p className="page__note">{user.name}</p>
        </aside>
      </div>
    </>
  );
}
